'use client';

import type { Breadcrumb } from '@eliadmin/core/src/api/products/types/product-types';
import { BreadcrumbsSchema } from '@eliadmin/core/src/components/seo/breadcrumbs-schema';
import { BREADCRUMBS_CONFIG } from '@eliadmin/core/src/constants/breadcrumbs-config';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import {
  arePathsEqual,
  getFlatBreadcrumbs,
  getSlugFromPage,
} from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { usePathname } from 'next/navigation';
import { GoHome } from 'react-icons/go';
import { MdArrowRight } from 'react-icons/md';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Paragraph } from '../ui/paragraph';

export type BreadcrumbsProps = {
  className?: {
    wrapper?: string;
    list?: string;
    item?: string;
    itemActive?: string;
    link?: string;
  };
  homeText?: string;
  breadcrumb: Breadcrumb;
  variant?: 'default' | 'secondary';
  product?: { name: string };
};

export function Breadcrumbs({
  breadcrumb,
  className,
  variant = 'default',
  product,
}: BreadcrumbsProps) {
  const pathname = usePathname();
  const { t } = useTranslations();

  const breadcrumbs = getFlatBreadcrumbs(breadcrumb);

  if (BREADCRUMBS_CONFIG.noDisplay.filter((el: string) => el === pathname).length) {
    return <nav className="hidden"></nav>;
  }

  return (
    <>
      <BreadcrumbsSchema breadcrumb={breadcrumb} />
      <nav
        className={cn(
          'hide-scroll-bar w-full overflow-x-scroll rounded-none bg-transparent',
          'ea-breadcrumbs',
          className?.wrapper,
        )}
      >
        <ol
          className={cn(
            'flex flex-row flex-nowrap items-center gap-0',
            'ea-breadcrumbs__list',
            className?.list,
          )}
        >
          <li
            className={cn(
              {
                [cn(
                  'pointer-events-none',
                  variant === 'secondary' ? 'text-white' : 'text-black',
                  'ea_breadcrumbs__item--active',
                  className?.itemActive,
                )]: arePathsEqual(pathname, getSlugFromPage('home')),
              },
              'ea_breadcrumbs__item',
              className?.item,
            )}
          >
            <Link
              href={getSlugFromPage('home') ?? '/'}
              className={cn('ea-breadcrumbs__link', className?.link)}
              prefetch={false}
              aria-label={t('text160')}
            >
              <GoHome size={20} className="text-primary" aria-hidden />
            </Link>
          </li>
          {breadcrumbs.map((breadcrumb, index) => {
            const isActive = arePathsEqual(pathname, breadcrumb.slug ?? '');
            return (
              <li key={index} className="flex w-auto items-center justify-start whitespace-nowrap">
                <span className={cn(className?.item)}>
                  <MdArrowRight size={20} className="text-border" aria-hidden />
                </span>
                <span
                  className={cn(
                    {
                      [cn(
                        'pointer-events-none',
                        variant === 'secondary' ? 'text-white' : 'text-black',
                        'ea_breadcrumbs__item--active',
                        className?.itemActive,
                      )]: isActive,
                      [cn(variant === 'secondary' ? 'text-secondary' : 'text-primary')]: !isActive,
                    },
                    'ea_breadcrumbs__item',
                    className?.item,
                  )}
                >
                  <Link
                    href={breadcrumb.slug ?? '#'}
                    className={cn(
                      'text-[13px] font-normal leading-[130%] sm:text-sm sm:leading-[130%]',
                      'ea-breadcrumbs__link',
                      className?.link,
                    )}
                    prefetch={false}
                  >
                    {breadcrumb.name}
                  </Link>
                </span>
              </li>
            );
          })}
          {!!product && (
            <li className="flex w-auto items-center justify-start whitespace-nowrap">
              <span className={cn(className?.item)}>
                <MdArrowRight size={20} className="text-border" />
              </span>
              <Paragraph size="base" color="black">
                {product.name}
              </Paragraph>
            </li>
          )}
        </ol>
      </nav>
    </>
  );
}
